import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  HMIDataType,
  HMIField,
  HMIFieldRule,
  HMIRuleClause,
  HMIRuleOperand,
  HMIRuleOperator,
} from '../../types';
import { defaultRuleValueFor, operatorOptionsFor } from './fieldOptions';

export type RuleDraft = Omit<HMIFieldRule, 'id'>;

export interface RuleEditorDialogProps {
  /** Present when editing an existing rule; absent when adding a new one. */
  rule?: HMIFieldRule;
  /** Every field in the template - the rule can reference any of them, regardless of section/tab. */
  fields: HMIField[];
  onCancel: () => void;
  onSave: (draft: RuleDraft) => void;
  onDelete?: () => void;
}

function fieldById(fields: HMIField[], id: string): HMIField | undefined {
  return fields.find((f) => f.id === id);
}

function makeClause(fields: HMIField[], fieldId?: string): HMIRuleClause {
  const field = (fieldId && fieldById(fields, fieldId)) || fields[0];
  return {
    fieldId: field?.id ?? '',
    operator: '==',
    compareTo: { kind: 'value', value: field ? defaultRuleValueFor(field.datatype, field.options) : '' },
  };
}

interface ClauseEditorProps {
  clause: HMIRuleClause;
  fields: HMIField[];
  onChange: (next: HMIRuleClause) => void;
  onRemove?: () => void;
}

/** One `<field> <operator> <value-or-other-field>` row, shared by the "when" and "then require" sections. */
function ClauseEditor({ clause, fields, onChange, onRemove }: ClauseEditorProps) {
  const field = fieldById(fields, clause.fieldId);
  const datatype: HMIDataType = field?.datatype ?? 'f64';
  const operatorOptions = operatorOptionsFor(datatype);

  const handleFieldChange = (fieldId: string) => {
    const nextField = fieldById(fields, fieldId);
    const nextOperators = operatorOptionsFor(nextField?.datatype ?? 'f64');
    const operator = nextOperators.some((o) => o.value === clause.operator)
      ? clause.operator
      : nextOperators[0].value;
    onChange({
      fieldId,
      operator,
      compareTo:
        clause.compareTo.kind === 'field'
          ? clause.compareTo
          : { kind: 'value', value: defaultRuleValueFor(nextField?.datatype ?? 'f64', nextField?.options) },
    });
  };

  const handleCompareKindChange = (kind: 'value' | 'field') => {
    if (kind === 'field') {
      const other = fields.find((f) => f.id !== clause.fieldId) ?? fields[0];
      onChange({ ...clause, compareTo: { kind: 'field', fieldId: other?.id ?? '' } });
    } else {
      onChange({ ...clause, compareTo: { kind: 'value', value: defaultRuleValueFor(datatype, field?.options) } });
    }
  };

  const handleCompareValueChange = (value: string | number | boolean) => {
    onChange({ ...clause, compareTo: { kind: 'value', value } });
  };

  const handleCompareFieldChange = (fieldId: string) => {
    onChange({ ...clause, compareTo: { kind: 'field', fieldId } });
  };

  return (
    <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Grid item xs={12} sm={3}>
        <TextField
          select
          fullWidth
          size="small"
          label="Field"
          value={clause.fieldId}
          onChange={(e) => handleFieldChange(e.target.value)}
        >
          {fields.map((f) => (
            <MenuItem key={f.id} value={f.id}>
              {f.caption}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={6} sm={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="Operator"
          value={clause.operator}
          onChange={(e) => onChange({ ...clause, operator: e.target.value as HMIRuleOperator })}
        >
          {operatorOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={6} sm={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="Compare to"
          value={clause.compareTo.kind}
          onChange={(e) => handleCompareKindChange(e.target.value as 'value' | 'field')}
        >
          <MenuItem value="value">A fixed value</MenuItem>
          <MenuItem value="field" disabled={fields.length < 2}>
            Another field
          </MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={10} sm={4}>
        <CompareValueInput
          datatype={datatype}
          options={field?.options}
          compareTo={clause.compareTo}
          fields={fields}
          onValueChange={handleCompareValueChange}
          onFieldChange={handleCompareFieldChange}
        />
      </Grid>
      <Grid item xs={2} sm={1}>
        {onRemove && (
          <Tooltip title="Remove condition">
            <IconButton size="small" onClick={onRemove}>
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Grid>
    </Grid>
  );
}

interface CompareValueInputProps {
  datatype: HMIDataType;
  options: HMIField['options'];
  compareTo: HMIRuleOperand;
  fields: HMIField[];
  onValueChange: (value: string | number | boolean) => void;
  onFieldChange: (fieldId: string) => void;
}

/** Renders the right-hand side of a clause: either another field picker, or a value input shaped to the left field's data type. */
function CompareValueInput({
  datatype,
  options,
  compareTo,
  fields,
  onValueChange,
  onFieldChange,
}: CompareValueInputProps) {
  if (compareTo.kind === 'field') {
    return (
      <TextField
        select
        fullWidth
        size="small"
        label="Field"
        value={compareTo.fieldId}
        onChange={(e) => onFieldChange(e.target.value)}
      >
        {fields.map((f) => (
          <MenuItem key={f.id} value={f.id}>
            {f.caption}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (datatype === 'bool') {
    return (
      <TextField
        select
        fullWidth
        size="small"
        label="Value"
        value={String(compareTo.value)}
        onChange={(e) => onValueChange(e.target.value === 'true')}
      >
        <MenuItem value="true">Yes / true</MenuItem>
        <MenuItem value="false">No / false</MenuItem>
      </TextField>
    );
  }

  if (datatype === 'enum' && options && options.length > 0) {
    return (
      <TextField
        select
        fullWidth
        size="small"
        label="Value"
        value={String(compareTo.value)}
        onChange={(e) => onValueChange(e.target.value)}
      >
        {options.map((o) => (
          <MenuItem key={String(o.value)} value={String(o.value)}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  const isNumeric = datatype === 'i32' || datatype === 'f32' || datatype === 'f64';
  return (
    <TextField
      fullWidth
      size="small"
      label="Value"
      type={isNumeric ? 'number' : 'text'}
      value={compareTo.value as string | number}
      onChange={(e) => onValueChange(isNumeric ? Number(e.target.value) : e.target.value)}
    />
  );
}

/**
 * Add / edit / delete form for one `HMIFieldRule` - a cross-field dependency
 * or validation rule. Covers every scenario the simpler per-field
 * `enabledWhen` can't: comparisons between two fields' values, mutual
 * exclusivity, and conditional constraints ("if X then Y").
 */
function RuleEditorDialog({ rule, fields, onCancel, onSave, onDelete }: RuleEditorDialogProps) {
  const isEditing = Boolean(rule);
  const [description, setDescription] = useState(rule?.description ?? '');
  const [when, setWhen] = useState<HMIRuleClause[]>(rule?.when ?? []);
  const [constraint, setConstraint] = useState<HMIRuleClause>(rule?.constraint ?? makeClause(fields));
  const [message, setMessage] = useState(rule?.message ?? '');

  const addCondition = () => setWhen((prev) => [...prev, makeClause(fields)]);
  const updateCondition = (index: number, next: HMIRuleClause) =>
    setWhen((prev) => prev.map((c, i) => (i === index ? next : c)));
  const removeCondition = (index: number) => setWhen((prev) => prev.filter((_, i) => i !== index));

  const isValid = fields.length > 0 && Boolean(constraint.fieldId);

  const handleSave = () => {
    if (!isValid) return;
    onSave({
      description: description.trim() || undefined,
      when: when.length > 0 ? when : undefined,
      constraint,
      message: message.trim() || undefined,
    });
  };

  if (fields.length === 0) {
    return (
      <Dialog open onClose={onCancel} fullWidth maxWidth="sm">
        <DialogTitle>Add rule</DialogTitle>
        <DialogContent>
          <Alert severity="info">Add at least one field before defining a dependency rule.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="md">
      <DialogTitle>{isEditing ? 'Edit rule' : 'Add rule'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            fullWidth
            size="small"
            label="Description (optional)"
            placeholder="e.g. Pressure must stay below the temperature threshold"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              If all of these are true (optional)
            </Typography>
            {when.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No conditions - the rule below always applies.
              </Typography>
            )}
            {when.map((clause, idx) => (
              <ClauseEditor
                key={idx}
                clause={clause}
                fields={fields}
                onChange={(next) => updateCondition(idx, next)}
                onRemove={() => removeCondition(idx)}
              />
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={addCondition}>
              Add condition
            </Button>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Then this must be true
            </Typography>
            <ClauseEditor clause={constraint} fields={fields} onChange={setConstraint} />
          </Box>

          <TextField
            fullWidth
            size="small"
            label="Custom error message (optional)"
            placeholder="Shown to the operator when this rule is violated"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Box>
          {isEditing && onDelete && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
              Delete rule
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="contained" disabled={!isValid} onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default RuleEditorDialog;
